import { useEffect, useState } from 'react';
import { AffectedAreas, type AffectedAreaRow } from '../../lib/api';
import { confirmDialog, alertDialog } from '../../components/ui/confirm';

export function AffectedAreasAdminPage() {
  const [areas, setAreas] = useState<AffectedAreaRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const r = await AffectedAreas.list(true);
      setAreas(r.affectedAreas);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);

  async function create() {
    if (!newName.trim() || creating) return;
    setCreating(true);
    setError(null);
    try {
      await AffectedAreas.create({ name: newName.trim() });
      setNewName('');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Create failed');
    } finally {
      setCreating(false);
    }
  }

  async function rename(area: AffectedAreaRow, name: string) {
    if (!name.trim() || name === area.name) return;
    try {
      await AffectedAreas.update(area.id, { name: name.trim() });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Rename failed');
    }
  }

  async function toggleActive(area: AffectedAreaRow) {
    try {
      await AffectedAreas.update(area.id, { isActive: !area.isActive });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Update failed');
    }
  }

  async function remove(area: AffectedAreaRow) {
    const ok = await confirmDialog({
      title: 'Delete affected area?',
      body: `"${area.name}" — if it's used by tickets it will be deactivated instead of deleted.`,
      tone: 'danger',
      confirmLabel: 'Delete',
    });
    if (!ok) return;
    try {
      const r = await AffectedAreas.remove(area.id);
      if (r && typeof r === 'object' && 'deactivated' in r) {
        await alertDialog({ title: 'Area deactivated', body: r.message });
      }
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    }
  }

  const sorted = [...areas].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-lg font-semibold text-slate-900">Affected areas</h1>
        <p className="text-sm text-slate-500">
          Used on tickets to tag which subsystem is impacted. Deactivated areas stay readable on
          historical tickets but are hidden from the picker on new tickets.
        </p>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="bg-white border border-slate-200 rounded-lg p-3 flex flex-col sm:flex-row gap-2">
        <input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && create()}
          placeholder="New area name (e.g. Quoting)"
          className="input flex-1"
        />
        <button
          onClick={create}
          disabled={!newName.trim() || creating}
          className="px-4 py-2 rounded-md bg-slate-900 text-white text-sm hover:bg-slate-800 disabled:opacity-60"
        >
          {creating ? 'Adding…' : 'Add area'}
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : sorted.length === 0 ? (
        <p className="text-sm text-slate-500">No affected areas yet.</p>
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-left">
              <tr>
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2 w-24">Status</th>
                <th className="px-3 py-2 w-40 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((a) => (
                <tr key={a.id} className="border-t border-slate-100">
                  <td className="px-3 py-2">
                    <NameEditor
                      initial={a.name}
                      disabled={!a.isActive}
                      onCommit={(name) => rename(a, name)}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <button
                      onClick={() => toggleActive(a)}
                      className={`px-2 py-0.5 rounded text-xs font-medium ${
                        a.isActive
                          ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200'
                          : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
                      }`}
                    >
                      {a.isActive ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => remove(a)}
                      className="text-xs text-red-600 hover:text-red-700"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function NameEditor({
  initial,
  disabled,
  onCommit,
}: {
  initial: string;
  disabled?: boolean;
  onCommit: (name: string) => void;
}) {
  const [v, setV] = useState(initial);
  useEffect(() => setV(initial), [initial]);
  return (
    <input
      value={v}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => { if (v.trim() && v !== initial) onCommit(v); else setV(initial); }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        if (e.key === 'Escape') setV(initial);
      }}
      disabled={disabled}
      className={`input ${disabled ? 'text-slate-500 italic' : ''}`}
    />
  );
}
